import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TrackMap, { type TrackPoint } from "../components/TrackMap";
import {
  WILDLIFE_RESCUE_INFO_INTRO,
  WILDLIFE_RESCUE_DISCLAIMER,
  WILDLIFE_RESCUE_ORDINANCES,
} from "@shared/wildlifeRescue";

type WeatherSnapshot = {
  temperature: number;
  windSpeed: number;
  windGust?: number;
  rain: number;
  cloudCover: number;
  humidity: number;
  kpIndex?: number;
};

type DjiLogMeta = {
  status: "parsed" | "undecryptable" | "error";
  fileName: string;
  droneName?: string;
  maxAltitudeM?: number;
  durationSec?: number;
  totalDistanceM?: number;
  batteryStartPercent?: number;
  batteryEndPercent?: number;
  batteryMinPercent?: number;
  trackPointCount?: number;
  version?: number;
};

type TrafficLogAircraft = {
  icao24: string;
  callsign: string;
  firstSeen: number;
  lastSeen: number;
  minAltitudeM: number;
  maxAltitudeM: number;
  closestDistanceKm: number;
  zone: "inner" | "outer" | "approach";
};

type TrafficLog = {
  startTime: number;
  endTime: number | null;
  innerRadiusM: number;
  outerRadiusM: number;
  aircraft: TrafficLogAircraft[];
};

type PublicEntry = {
  id: string;
  droneName: string;
  droneClass: string;
  flightDate: string;
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  takeoffTime: string | null;
  landingTime: string | null;
  flightDurationMinutes: number | null;
  maxAltitude: number | null;
  weatherConditions: WeatherSnapshot | null;
  incidentOccurred: boolean;
  hasRestrictions?: boolean | null;
  restrictionNotes?: string | null;
  notes: string | null;
  status?: string | null;
  gpsTrack?: TrackPoint[] | null;
  trafficLog?: TrafficLog | unknown | null;
  djiLog?: DjiLogMeta | null;
  djiLogs?: DjiLogMeta[] | null;
  officialMapCrossCheck?: { blocking: boolean; note?: string } | null;
  wildlifeRescueMode?: boolean | null;
  attachedDocuments?: PublicAttachedDocument[] | null;
  pilot: { name?: string | null; operatorId?: string | null } | null;
};

type PublicAttachedDocument = {
  type: "av" | "a2" | "insurance";
  label: string;
  bundesland?: string;
  expirationDate?: string;
  matchedExactly: boolean;
  hasFile: boolean;
  downloadUrl?: string;
};

const ZONE_LABEL: Record<TrafficLogAircraft["zone"], string> = {
  inner: "Inner",
  outer: "Outer",
  approach: "Annäherung",
};

function fmtTs(ts: number) {
  return new Date(ts).toLocaleString("de-DE");
}

export default function PublicProtocol() {
  const { token } = useParams<{ token: string }>();
  const [entry, setEntry] = useState<PublicEntry | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "notfound" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState("loading");
      try {
        const res = await fetch(`/api/public/protocol/${encodeURIComponent(token || "")}`);
        if (cancelled) return;
        if (res.status === 404) {
          setState("notfound");
          return;
        }
        if (!res.ok) {
          setState("error");
          return;
        }
        const data = (await res.json()) as PublicEntry;
        if (cancelled) return;
        setEntry(data);
        setState("ok");
      } catch {
        if (!cancelled) setState("error");
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state === "loading") {
    return <Frame><div className="text-center text-[var(--color-app-text-secondary)] py-16">Lädt …</div></Frame>;
  }

  if (state === "notfound") {
    return (
      <Frame>
        <div className="card text-center py-12">
          <h1 className="text-lg font-semibold text-[var(--color-app-text)] mb-2">Protokoll nicht verfügbar</h1>
          <p className="text-sm text-[var(--color-app-text-secondary)]">
            Dieser Freigabe-Link ist nicht mehr gültig. Der Eigentümer hat die Freigabe
            möglicherweise deaktiviert.
          </p>
        </div>
      </Frame>
    );
  }

  if (state === "error" || !entry) {
    return (
      <Frame>
        <div className="card text-center py-12">
          <h1 className="text-lg font-semibold text-[var(--color-app-text)] mb-2">Fehler</h1>
          <p className="text-sm text-[var(--color-app-text-secondary)]">Das Protokoll konnte nicht geladen werden.</p>
        </div>
      </Frame>
    );
  }

  const weather = entry.weatherConditions || null;
  const track: TrackPoint[] = Array.isArray(entry.gpsTrack) ? entry.gpsTrack : [];
  const djiLogs: DjiLogMeta[] =
    entry.djiLogs && entry.djiLogs.length > 0
      ? entry.djiLogs
      : entry.djiLog
        ? [entry.djiLog]
        : [];
  const trafficRaw: unknown = entry.trafficLog || null;
  const trafficSession: TrafficLog | null =
    trafficRaw && !Array.isArray(trafficRaw) && typeof trafficRaw === "object" && "aircraft" in (trafficRaw as object)
      ? (trafficRaw as TrafficLog)
      : null;

  return (
    <Frame>
      <div className="space-y-6">
        <div>
          <p className="text-[var(--color-app-text-secondary)] text-sm">{entry.flightDate} · {entry.droneName}</p>
          <h1 className="text-xl font-semibold text-[var(--color-app-text)] mt-1">Flugprotokoll</h1>
        </div>

        {entry.pilot && (entry.pilot.name || entry.pilot.operatorId) ? (
          <div className="card">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Pilot</h2>
            {entry.pilot.name ? <Row label="Name" value={entry.pilot.name} /> : null}
            {entry.pilot.operatorId ? <Row label="Betreiber-Nr." value={entry.pilot.operatorId} /> : null}
          </div>
        ) : null}

        <div className="card">
          <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Drohne</h2>
          <Row label="Name" value={entry.droneName} />
          <Row label="Klasse" value={entry.droneClass} />
        </div>

        <div className="card">
          <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Flug</h2>
          <Row label="Datum" value={entry.flightDate} />
          <Row label="Ort" value={entry.locationName || "—"} />
          {entry.latitude != null && entry.longitude != null ? (
            <Row label="Koordinaten" value={`${entry.latitude.toFixed(5)}, ${entry.longitude.toFixed(5)}`} />
          ) : null}
          <Row label="Start" value={entry.takeoffTime ? new Date(entry.takeoffTime).toLocaleString("de-DE") : "—"} />
          <Row label="Landung" value={entry.landingTime ? new Date(entry.landingTime).toLocaleString("de-DE") : "—"} />
          <Row label="Dauer" value={entry.flightDurationMinutes ? `${entry.flightDurationMinutes} min` : "—"} />
          <Row label="Max. Höhe" value={entry.maxAltitude ? `${entry.maxAltitude} m AGL` : "—"} />
          <Row label="Vorfall" value={entry.incidentOccurred ? "Ja" : "Nein"} highlight={entry.incidentOccurred} />
        </div>

        {entry.hasRestrictions ? (
          <div className="card">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Einschränkungen</h2>
            <p className="text-sm text-[var(--color-caution)]">{entry.restrictionNotes || "Einschränkungen vorhanden."}</p>
          </div>
        ) : null}

        {entry.officialMapCrossCheck ? (
          <div
            className="card"
            data-testid="box-crosscheck"
            style={{
              borderLeft: `4px solid ${entry.officialMapCrossCheck.blocking ? "var(--color-caution)" : "var(--color-go)"}`,
            }}
          >
            <p
              className="text-sm font-semibold"
              style={{ color: entry.officialMapCrossCheck.blocking ? "var(--color-caution)" : "var(--color-go)" }}
            >
              Offizielle Karte gegengeprüft:{" "}
              {entry.officialMapCrossCheck.blocking
                ? "startverhindernde Geozonen"
                : "keine startverhindernden Geozonen"}
            </p>
            {entry.officialMapCrossCheck.note ? (
              <p className="text-sm text-[var(--color-app-text-secondary)] mt-1">
                Notiz: {entry.officialMapCrossCheck.note}
              </p>
            ) : null}
          </div>
        ) : null}

        {entry.wildlifeRescueMode ? (
          <div className="card" data-testid="box-wildlife-rescue">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">
              Allgemeinverfügungen zur Wildtierrettung
            </h2>
            <p className="text-xs text-[var(--color-app-text-secondary)] mb-3">
              {WILDLIFE_RESCUE_INFO_INTRO}
            </p>
            <div className="space-y-3">
              {WILDLIFE_RESCUE_ORDINANCES.map((ord) => (
                <div
                  key={ord.id}
                  className="rounded-xl border border-[var(--color-app-border)] bg-[var(--color-app-bg-secondary)] p-4"
                  data-testid={`card-wildlife-ordinance-${ord.id}`}
                >
                  <div className="text-[13px] font-bold text-brand-600">{ord.authority}</div>
                  <div className="text-[15px] font-semibold text-[var(--color-app-text)] mt-0.5 mb-2">
                    {ord.title}
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--color-app-text-secondary)]">
                    Geltungsbereich
                  </div>
                  <p className="text-sm text-[var(--color-app-text)] mt-1 mb-2">{ord.scope}</p>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--color-app-text-secondary)]">
                    Wichtige Auflagen
                  </div>
                  <ul className="text-sm text-[var(--color-app-text)] mt-1 list-disc pl-5 space-y-1">
                    {ord.conditions.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                  <a
                    href={ord.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-sm font-semibold text-brand-600 hover:underline"
                    data-testid={`link-wildlife-source-${ord.id}`}
                  >
                    Quelle: {ord.sourceLabel}
                  </a>
                </div>
              ))}
            </div>
            <p className="text-xs italic text-[var(--color-app-text-tertiary)] mt-3">
              {WILDLIFE_RESCUE_DISCLAIMER}
            </p>
          </div>
        ) : null}

        {entry.attachedDocuments && entry.attachedDocuments.length > 0 ? (
          <div className="card" data-testid="box-attached-docs">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Hinterlegte Dokumente</h2>
            <p className="text-xs text-[var(--color-app-text-secondary)] mb-3">
              Zum Flugzeitpunkt gültige Pilotendokumente.
            </p>
            <div className="space-y-2">
              {entry.attachedDocuments.map((doc, i) => (
                <div
                  key={`${doc.type}-${doc.bundesland ?? ""}-${i}`}
                  data-testid={`row-doc-${doc.type}`}
                  className="flex items-center justify-between gap-3 py-1"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[var(--color-app-text)] truncate">{doc.label}</p>
                    <p className="text-xs text-[var(--color-app-text-secondary)]">
                      {doc.matchedExactly ? "Zum Flugzeitpunkt gültige Fassung" : "Bestmögliche Fassung"}
                      {doc.expirationDate ? ` · gültig bis ${doc.expirationDate}` : ""}
                    </p>
                  </div>
                  {doc.hasFile && doc.downloadUrl ? (
                    <a
                      href={doc.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 px-3 py-1 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:opacity-80 transition"
                      data-testid={`link-doc-${doc.type}`}
                    >
                      Ansehen
                    </a>
                  ) : (
                    <span className="shrink-0 text-xs text-[var(--color-app-text-secondary)]">Nur Metadaten</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="card">
          <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Wetter-Snapshot</h2>
          {weather ? (
            <>
              <Row label="Temperatur" value={`${weather.temperature.toFixed(1)} °C`} />
              <Row label="Wind" value={`${weather.windSpeed.toFixed(1)} m/s`} />
              <Row label="Böen" value={weather.windGust != null ? `${weather.windGust.toFixed(1)} m/s` : "—"} />
              <Row label="Niederschlag" value={`${weather.rain ?? 0} mm/h`} />
              <Row label="Bewölkung" value={`${weather.cloudCover ?? 0} %`} />
              <Row label="Luftfeuchte" value={`${weather.humidity ?? 0} %`} />
              {weather.kpIndex != null ? <Row label="Kp-Index" value={`${weather.kpIndex}`} /> : null}
            </>
          ) : (
            <p className="text-sm text-[var(--color-app-text-secondary)]">Kein Wetter-Snapshot für diesen Flug gespeichert.</p>
          )}
        </div>

        <div className="card">
          <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">GPS-Track</h2>
          {track.length >= 2 ? (
            <>
              <p className="text-xs text-[var(--color-app-text-secondary)] mb-2">{track.length} Punkte aufgezeichnet</p>
              <TrackMap key={`track-${entry.id}-${track.length}`} track={track} fallbackLat={entry.latitude} fallbackLon={entry.longitude} />
            </>
          ) : entry.latitude != null && entry.longitude != null ? (
            <>
              <p className="text-xs text-[var(--color-app-text-secondary)] mb-2">Kein detaillierter Track verfügbar — Startposition wird angezeigt.</p>
              <TrackMap key={`fallback-${entry.id}`} track={[]} fallbackLat={entry.latitude} fallbackLon={entry.longitude} />
            </>
          ) : (
            <p className="text-sm text-[var(--color-app-text-secondary)]">Keine GPS-Daten für diesen Flug gespeichert.</p>
          )}
        </div>

        {djiLogs.length > 0 ? (
          <div className="card">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">DJI-Flugprotokoll</h2>
            {djiLogs.map((log, i) => (
              <div key={`${log.fileName}-${i}`} className={i > 0 ? "mt-4 pt-4 border-t border-[var(--color-app-border)]" : ""}>
                {djiLogs.length > 1 ? <p className="text-sm font-medium text-[var(--color-app-text)] mb-1">Log {i + 1}</p> : null}
                {log.droneName ? <Row label="Drohne (Log)" value={log.droneName} /> : null}
                {log.version != null ? <Row label="Log-Version" value={`v${log.version}`} /> : null}
                {log.maxAltitudeM != null ? <Row label="Max. Höhe (Log)" value={`${log.maxAltitudeM.toFixed(1)} m`} /> : null}
                {log.durationSec != null ? <Row label="Flugdauer (Log)" value={`${Math.round(log.durationSec)} s`} /> : null}
                {log.totalDistanceM != null ? <Row label="Strecke (Log)" value={`${(log.totalDistanceM / 1000).toFixed(2)} km`} /> : null}
                {log.batteryStartPercent != null || log.batteryEndPercent != null || log.batteryMinPercent != null ? (
                  <Row
                    label="Akku (Start/Ende/Min)"
                    value={[
                      log.batteryStartPercent != null ? `${log.batteryStartPercent}%` : "—",
                      log.batteryEndPercent != null ? `${log.batteryEndPercent}%` : "—",
                      log.batteryMinPercent != null ? `${log.batteryMinPercent}%` : "—",
                    ].join(" / ")}
                  />
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {trafficSession ? (
          <div className="card">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Verkehrslog (ADS-B / OGN)</h2>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <Row label="Aufzeichnung ab" value={fmtTs(trafficSession.startTime)} />
              <Row label="Aufzeichnung bis" value={trafficSession.endTime ? fmtTs(trafficSession.endTime) : "—"} />
            </div>
            {trafficSession.aircraft.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[var(--color-app-text-secondary)] border-b border-[var(--color-app-border)]">
                      <th className="py-1.5 pr-2">Callsign</th>
                      <th className="py-1.5 pr-2">ICAO</th>
                      <th className="py-1.5 pr-2">Zone</th>
                      <th className="py-1.5 pr-2">Min Distanz</th>
                      <th className="py-1.5">Höhe (min/max)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trafficSession.aircraft.map((a) => (
                      <tr key={a.icao24} className="border-b border-[var(--color-app-border)]">
                        <td className="py-1.5 pr-2 font-medium text-[var(--color-app-text)]">{a.callsign || "—"}</td>
                        <td className="py-1.5 pr-2 font-mono text-[var(--color-app-text-secondary)]">{a.icao24}</td>
                        <td className="py-1.5 pr-2">{ZONE_LABEL[a.zone]}</td>
                        <td className="py-1.5 pr-2">{a.closestDistanceKm.toFixed(2)} km</td>
                        <td className="py-1.5">{Math.round(a.minAltitudeM)} – {Math.round(a.maxAltitudeM)} m</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[var(--color-app-text-secondary)]">Keine Luftfahrzeuge während des Fluges erfasst.</p>
            )}
          </div>
        ) : null}

        {entry.notes ? (
          <div className="card">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Notizen</h2>
            <p className="text-sm text-[var(--color-app-text)] whitespace-pre-wrap">{entry.notes}</p>
          </div>
        ) : null}

        <p className="text-center text-xs text-[var(--color-app-text-tertiary)] pt-2">
          Öffentlich freigegebenes Flugprotokoll · schreibgeschützt
        </p>
      </div>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-app-bg-secondary)]">
      <div className="max-w-2xl mx-auto px-4 py-8">{children}</div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm py-1">
      <div className="text-[var(--color-app-text-secondary)]">{label}</div>
      <div className={`col-span-2 ${highlight ? "text-[var(--color-nogo)] font-semibold" : "text-[var(--color-app-text)]"}`}>{value}</div>
    </div>
  );
}
